import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Ruler, Maximize2, X, ChevronLeft, ChevronRight } from 'lucide-react';

import planoBoulder from '@/assets/projects/plano-boulder-25m.webp';
import planoSpraywall from '@/assets/projects/plano-spraywall-8m.webp';

const DesignShowcaseSection: React.FC = () => {
  const { t } = useLanguage();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const designs = [
    {
      image: planoBoulder,
      title: t('home.designs.1.title'),
      description: t('home.designs.1.description'),
      specs: [
        { label: t('home.designs.spec.type'), value: 'Muro Boulder Interior' },
        { label: t('home.designs.spec.height'), value: '4,20 m' },
        { label: t('home.designs.spec.width'), value: '25,00 m' },
        { label: t('home.designs.spec.depth'), value: '1,60 m' },
      ],
      alt: 'Plano técnico 3D muro de escalada boulder 25 metros Rock-Line',
    },
    {
      image: planoSpraywall,
      title: t('home.designs.2.title'),
      description: t('home.designs.2.description'),
      specs: [
        { label: t('home.designs.spec.type'), value: 'Spray Wall Interior' },
        { label: t('home.designs.spec.height'), value: '4,20 m' },
        { label: t('home.designs.spec.width'), value: '8,00 m' },
        { label: t('home.designs.spec.inclination'), value: '10°' },
      ],
      alt: 'Plano técnico 3D spray wall escalada boulder Rock-Line',
    },
  ];

  const navigateLightbox = useCallback((direction: 'prev' | 'next') => {
    if (lightboxIndex === null) return;
    if (direction === 'prev' && lightboxIndex > 0) {
      setLightboxIndex(lightboxIndex - 1);
    } else if (direction === 'next' && lightboxIndex < designs.length - 1) {
      setLightboxIndex(lightboxIndex + 1);
    }
  }, [lightboxIndex, designs.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') navigateLightbox('prev');
      if (e.key === 'ArrowRight') navigateLightbox('next');
      if (e.key === 'Escape') setLightboxIndex(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, navigateLightbox]);

  return (
    <>
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-2 mb-4">
              <Ruler className="h-5 w-5 text-primary" />
              <span className="text-primary font-medium text-sm">{t('home.designs.badge')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('home.designs.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('home.designs.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {designs.map((design, index) => (
              <motion.div
                key={index}
                className="group bg-card rounded-2xl overflow-hidden border border-border shadow-sm hover:shadow-lg transition-shadow duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <div
                  className="relative cursor-pointer overflow-hidden"
                  onClick={() => setLightboxIndex(index)}
                >
                  <img
                    src={design.image}
                    alt={design.alt}
                    className="w-full h-auto group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-secondary/0 group-hover:bg-secondary/20 transition-colors duration-300 flex items-center justify-center">
                    <Maximize2 className="h-8 w-8 text-primary-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-foreground mb-2">{design.title}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{design.description}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {design.specs.map((spec, i) => (
                      <div key={i} className="bg-muted rounded-lg p-3">
                        <span className="text-xs text-muted-foreground block">{spec.label}</span>
                        <span className="text-sm font-semibold text-foreground">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightboxIndex(null)}
          >
            <button
              className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(null);
              }}
            >
              <X className="h-8 w-8" />
            </button>

            {lightboxIndex > 0 && (
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2 z-10 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox('prev');
                }}
              >
                <ChevronLeft className="h-8 w-8" />
              </button>
            )}

            {lightboxIndex < designs.length - 1 && (
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2 z-10 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox('next');
                }}
              >
                <ChevronRight className="h-8 w-8" />
              </button>
            )}

            <motion.img
              key={lightboxIndex}
              src={designs[lightboxIndex].image}
              alt={designs[lightboxIndex].alt}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DesignShowcaseSection;
